import React, { useState } from "react";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Delete-Button mit Confirm-Dialog und automatischem Cascade-Fallback.
 *
 * props:
 *   label       – Was wird gelöscht? z.B. "Immobilie Goethestraße 12"
 *   kind        – "entity" | "property" | "unit" | "tenant" (nur für Texte)
 *   onDelete    – async (cascade: boolean) => Promise   (muss API-Fehler durchreichen)
 *   onDeleted   – callback nach erfolgreicher Löschung
 *   testid      – data-testid für den Trigger-Button
 *   disabled    – optional
 *   grund       – warum gesperrt; steht als title am Trigger, damit die Auskunft
 *                 dort ist, wo geklickt wird (siehe useSchreibschutz)
 */
const DeleteConfirm = ({ label, kind = "datensatz", onDelete, onDeleted, testid, disabled, grund, size = "sm", variant = "ghost" }) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [needsCascade, setNeedsCascade] = useState(false);
    const [cascadeMessage, setCascadeMessage] = useState("");

    const kindLabel = {
        entity: "Entität",
        property: "Immobilie",
        unit: "Mieteinheit",
        tenant: "Mieter",
    }[kind] || "Datensatz";

    const reset = () => { setNeedsCascade(false); setCascadeMessage(""); };

    const run = async (cascade) => {
        setBusy(true);
        try {
            await onDelete(cascade);
            toast.success(`${kindLabel} gelöscht`);
            setOpen(false);
            reset();
            onDeleted?.();
        } catch (err) {
            const status = err.response?.status;
            const detail = err.response?.data?.detail || "Löschen fehlgeschlagen";
            if (status === 409) {
                setNeedsCascade(true);
                setCascadeMessage(detail);
            } else {
                toast.error(detail);
            }
        } finally { setBusy(false); }
    };

    return (
        <AlertDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <AlertDialogTrigger asChild>
                <Button variant={variant} size={size} className="h-8 text-gray-400 hover:text-red-600" disabled={disabled} data-testid={testid} title={grund || "Löschen"}>
                    <Trash2 size={12} />
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{kindLabel} löschen?</AlertDialogTitle>
                    <AlertDialogDescription>
                        {!needsCascade ? (
                            <>„{label}" wird <strong>unwiderruflich</strong> gelöscht.</>
                        ) : (
                            <>
                                <span className="text-red-700 font-medium">Abhängige Datensätze gefunden:</span>
                                <br />
                                {cascadeMessage}
                                <br /><br />
                                Bestätige, um <strong>{kindLabel} inklusive aller Abhängigkeiten</strong> zu löschen.
                            </>
                        )}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={busy}>Abbrechen</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={(e) => { e.preventDefault(); run(needsCascade); }}
                        className={needsCascade ? "bg-red-700 hover:bg-red-800" : "bg-red-600 hover:bg-red-700"}
                        disabled={busy}
                        data-testid={`${testid}-confirm`}
                    >
                        {busy ? "Lösche…" : needsCascade ? "Mit Abhängigkeiten löschen" : "Endgültig löschen"}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default DeleteConfirm;
